import {
  actionCard, filtersFormController, openModal, tablePageController, translate,
} from 'magner';
import { ackDocumentsRead, logAckInfo } from 'features/ack-documents/requests';
import { ACK_DOCUMENT_ROUTE, AckDocument } from 'features/ack-documents/types';
import { describeAudience } from 'features/ack-documents/utils/audience';
import { tGlobal } from 'features/ack-documents/utils/translate';
import AckDocumentTitle from 'features/ack-documents/components/ackDocumentTitle.vue';
import AckDocumentActions from 'features/ack-documents/components/ackDocumentActions.vue';
import CreateAckDocumentDialog from 'features/ack-documents/components/createAckDocumentDialog.vue';
import { formatDateTime } from 'features/ack-documents/utils/date';

const t = tGlobal;

const ratio = (value: number, total: number, className: string) => (
  `<span class="${className}">${value ?? 0} / ${total ?? 0}</span>`
);

/**
 * Ссылка на карточку документа. Не rowLink: при нём Magner оборачивает в router-link каждую ячейку,
 * включая меню «⋮», и клик по нему открывал карточку. Колонке «Действия» ссылку не ставим.
 */
const documentLink = (row: AckDocument) => ({
  name: ACK_DOCUMENT_ROUTE,
  params: { id: row.id },
});

// any: в фильтре и вычисляемых колонках есть поля, которых нет в AckDocument (как в user-activity)
const ackDocumentsFilters = filtersFormController<any>({
  actions: [],
  saveToLocalStorage: false,
  submitEvent: 'input',
  pagination: {
    items: 25,
    page: 1,
  },
  filtersInSeparatePanel: false,
  filtersData: {
    search: '',
  },
  sort: {},
  layout: [
    {
      type: 'input',
      name: 'search',
      props: {
        placeholder: translate('hubstr.ack_documents.table.search_placeholder'),
        // Своя иконка: у Search из @element-plus/icons-vue слишком короткая ручка
        prefixIcon: () => import('features/ack-documents/assets/search.svg'),
        inputDelay: 400,
        clearable: true,
      },
    },
  ],
});

const ackDocumentsConfig = tablePageController<any>({
  header: {
    title: translate('hubstr.ack_documents.table.title'),
    actions: [
      {
        type: 'action',
        props: {
          type: 'primary',
          text: translate('hubstr.ack_documents.table.add'),
        },
        emits: 'update-table',
        action: actionCard<any>(async () => {
          logAckInfo('create', 'open dialog');
          try {
            await openModal<any>({
              type: 'custom',
              handleBeforeClose: false,
              customClass: 'modal-max-width modal-max-width__800',
              component: () => CreateAckDocumentDialog,
              props: {},
            });
            return false;
          } catch (e) {
            // null — модалку закрыли без сохранения
            if (e !== null) {
              console.error('[AckDocuments.create] modal failed', e);
              return translate('hubstr.ack_documents.errors.common');
            }
            return false;
          }
        }),
      },
    ],
  },
  request: ackDocumentsRead,
  table: {
    tableLayout: 'auto',
    scrollbarAlwaysOn: true,
    emptyText: translate('hubstr.ack_documents.table.empty'),
    columns: [
      {
        prop: 'title',
        label: translate('hubstr.ack_documents.table.title_col'),
        columnLink: documentLink,
        minWidth: 260,
        view: {
          type: 'custom',
          component: () => AckDocumentTitle,
        },
      },
      {
        prop: 'createdAt',
        label: translate('hubstr.ack_documents.table.created_at'),
        columnLink: documentLink,
        width: 160,
        view: {
          type: 'text',
          formatter: (_, row) => (row.createdAt ? formatDateTime(row.createdAt) : '—'),
        },
      },
      {
        prop: 'createdBy',
        label: translate('hubstr.ack_documents.table.author'),
        columnLink: documentLink,
        width: 180,
        view: {
          type: 'text',
          formatter: (_, row) => row.createdBy?.fullName || '—',
        },
      },
      {
        prop: 'criteria',
        label: translate('hubstr.ack_documents.table.audience'),
        columnLink: documentLink,
        width: 200,
        view: {
          type: 'text',
          formatter: (_, row) => describeAudience(row.criteria, t),
        },
      },
      {
        prop: 'counters',
        label: translate('hubstr.ack_documents.table.assigned'),
        columnLink: documentLink,
        width: 130,
        view: {
          type: 'text',
          formatter: (_, row) => String(row.counters?.assigned ?? 0),
        },
      },
      {
        prop: 'acknowledged',
        label: translate('hubstr.ack_documents.table.acknowledged'),
        columnLink: documentLink,
        width: 130,
        view: {
          type: 'html',
          formatter: (_, row) => ratio(
            row.counters?.acknowledged,
            row.counters?.assigned,
            'ack-documents-ratio--success',
          ),
        },
      },
      {
        prop: 'notAcknowledged',
        label: translate('hubstr.ack_documents.table.not_acknowledged'),
        columnLink: documentLink,
        width: 140,
        view: {
          type: 'html',
          formatter: (_, row) => ratio(
            (row.counters?.notOpened ?? 0) + (row.counters?.opened ?? 0),
            row.counters?.assigned,
            'ack-documents-ratio--muted',
          ),
        },
      },
      {
        prop: 'id',
        label: translate('hubstr.ack_documents.table.actions'),
        width: 100,
        align: 'center',
        view: {
          type: 'custom',
          component: () => AckDocumentActions,
        },
      },
    ],
  },
  filters: ackDocumentsFilters,
});

export default ackDocumentsConfig;
